import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { isBackendUnavailableError } from '@/api/backendUnavailable.util.ts';
import { type UserDetailsWithPermissionDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { RefetchModel } from '@/api/model/Refetch.model.ts';
import { fetchCurrentUser } from '@/contexts/userContext/util/fetchCurrentUser.util.ts';

const BACKEND_WAIT_RETRY_DELAY_MS = 5_000;
const BACKEND_WAIT_RETRY_LIMIT = 36;

type Return = {
  errorGettingUser: AxiosError | null;
  getCurrentUser: RefetchModel<UserDetailsWithPermissionDto>;
  isGettingCurrentUser: boolean;
  isWaitingForBackend: boolean;
};

export const useGetCurrentUser = (): Return => {
  const { error, failureCount, fetchStatus, isLoading, refetch } = useQuery<
    UserDetailsWithPermissionDto,
    AxiosError
  >({
    enabled: false,
    queryFn: fetchCurrentUser,
    queryKey: [QueryKeyEnum.USERS.CURRENT],
    retry: (retriesSoFar, retryError) =>
      retriesSoFar < BACKEND_WAIT_RETRY_LIMIT && isBackendUnavailableError(retryError),
    retryDelay: BACKEND_WAIT_RETRY_DELAY_MS,
  });
  return {
    errorGettingUser: error,
    getCurrentUser: refetch,
    isGettingCurrentUser: fetchStatus !== 'idle' && isLoading,
    isWaitingForBackend: fetchStatus !== 'idle' && failureCount > 0,
  };
};
